import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";

// ─── Theme ────────────────────────────────────────────────────────
const Ctx = createContext(null);
const useT = () => useContext(Ctx);

const DARK = {
  bg:"#080610", surface:"#100e1c", card:"#16132a", border:"#2a2240",
  accent:"#d4a843", accentDim:"#6b520e", accentSoft:"rgba(212,168,67,0.12)",
  text:"#ede5cc", textDim:"#7a7060", textMuted:"#4a4538",
  purple:"#7c5fc0", purpleL:"#a88fe0",
  green:"#3db87a", greenDark:"#0c2a1a", greenBorder:"#1e5c38",
  canvas:"#04030c", inputBg:"#16132a",
  forgeGrad:"linear-gradient(135deg,#6b520e,#7c5fc0)",
  shadow:"0 4px 32px rgba(0,0,0,0.6)",
  isDark:true,
};
const LIGHT = {
  bg:"#faf7f0", surface:"#ffffff", card:"#f5f0e8", border:"#e0d8c8",
  accent:"#9a6800", accentDim:"#f0dfa0", accentSoft:"rgba(154,104,0,0.08)",
  text:"#1e1a10", textDim:"#6a6050", textMuted:"#b0a890",
  purple:"#5a3fa0", purpleL:"#7a5fc0",
  green:"#1a6a40", greenDark:"#e8f8ee", greenBorder:"#4aaa7a",
  canvas:"#f0ece0", inputBg:"#ffffff",
  forgeGrad:"linear-gradient(135deg,#d4a843,#7c5fc0)",
  shadow:"0 4px 32px rgba(0,0,0,0.12)",
  isDark:false,
};

// ─── Constants ────────────────────────────────────────────────────
const QWERTY=[["Q","W","E","R","T","Y","U","I","O","P"],["A","S","D","F","G","H","J","K","L"],["Z","X","C","V","B","N","M"]];
const ABC=[["A","B","C","D","E","F","G","H","I"],["J","K","L","M","N","O","P","Q","R"],["S","T","U","V","W","X","Y","Z"]];
const NUM=["0","1","2","3","4","5","6","7","8","9"];
const SYM=[".",",","!","?","'","-",":",";","@","#"];
const ALL_ALPHA="ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const ALL_CHARS=[...ALL_ALPHA,...NUM,...SYM];

const SHAPES=[
  {id:"circle",   svg:(c,s)=>`<circle cx="20" cy="20" r="16" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"square",   svg:(c,s)=>`<rect x="3" y="3" width="34" height="34" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"triangle", svg:(c,s)=>`<polygon points="20,3 37,37 3,37" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"diamond",  svg:(c,s)=>`<polygon points="20,2 38,20 20,38 2,20" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"star",     svg:(c,s)=>{const p=[...Array(10)].map((_,i)=>{const r=i%2===0?17:7,a=(i*36-90)*Math.PI/180;return `${20+r*Math.cos(a)},${20+r*Math.sin(a)}`;}).join(" ");return `<polygon points="${p}" stroke="${c}" stroke-width="${s}" fill="none"/>`;},},
  {id:"hexagon",  svg:(c,s)=>{const p=[...Array(6)].map((_,i)=>{const a=(i*60-30)*Math.PI/180;return `${20+17*Math.cos(a)},${20+17*Math.sin(a)}`;}).join(" ");return `<polygon points="${p}" stroke="${c}" stroke-width="${s}" fill="none"/>`;},},
  {id:"cross",    svg:(c,s)=>`<path d="M20,3 L20,37 M3,20 L37,20" stroke="${c}" stroke-width="${s}" stroke-linecap="round"/>`},
  {id:"wave",     svg:(c,s)=>`<path d="M2,20 Q8,8 14,20 Q20,32 26,20 Q32,8 38,20" stroke="${c}" stroke-width="${s}" fill="none" stroke-linecap="round"/>`},
  {id:"eye",      svg:(c,s)=>`<path d="M2,20 Q20,4 38,20 Q20,36 2,20Z" stroke="${c}" stroke-width="${s}" fill="none"/><circle cx="20" cy="20" r="5" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"spiral",   svg:(c,s)=>`<path d="M20,20 Q28,20 28,13 Q28,5 20,5 Q10,5 10,15 Q10,27 22,27 Q33,27 33,14" stroke="${c}" stroke-width="${s}" fill="none" stroke-linecap="round"/>`},
  {id:"heart",    svg:(c,s)=>`<path d="M20,32 C8,24 4,14 10,9 C14,5 18,8 20,12 C22,8 26,5 30,9 C36,14 32,24 20,32Z" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"arrow",    svg:(c,s)=>`<path d="M3,20 L32,20 M20,9 L33,20 L20,31" stroke="${c}" stroke-width="${s}" stroke-linecap="round" fill="none"/>`},
  {id:"overlap",  svg:(c,s)=>`<circle cx="14" cy="20" r="12" stroke="${c}" stroke-width="${s}" fill="none"/><circle cx="26" cy="20" r="12" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"crescent", svg:(c,s)=>`<path d="M28,5 A16,16 0 1 0 28,35 A12,12 0 1 1 28,5" stroke="${c}" stroke-width="${s}" fill="none"/>`},
  {id:"pentagon", svg:(c,s)=>{const p=[...Array(5)].map((_,i)=>{const a=(i*72-90)*Math.PI/180;return `${20+17*Math.cos(a)},${20+17*Math.sin(a)}`;}).join(" ");return `<polygon points="${p}" stroke="${c}" stroke-width="${s}" fill="none"/>`;},},
  {id:"zigzag",   svg:(c,s)=>`<path d="M2,26 L9,14 L16,26 L23,14 L30,26 L37,14" stroke="${c}" stroke-width="${s}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`},
];

const CATS=[
  {key:"phonology",label:"Phonology",icon:"🔊",ph:'e.g. "th" → "vr", vowels doubled',desc:"How sounds are spoken"},
  {key:"orthography",label:"Spelling",icon:"✍️",ph:"e.g. every 's' becomes 'zh'",desc:"How words are written"},
  {key:"morphology",label:"Word Rules",icon:"🧩",ph:'e.g. plurals add "-okh"',desc:"How words are built"},
  {key:"syntax",label:"Sentence Order",icon:"📐",ph:"e.g. verb comes first",desc:"Sentence structure"},
  {key:"lexical",label:"Vocab Swaps",icon:"📖",ph:'e.g. "water" → "vrael"',desc:"Word replacements"},
];

// ─── Transcript ───────────────────────────────────────────────────
function makeTranscript(result, rules) {
  if (!result) return "";
  const g = result.guide, line = "─".repeat(58), t = [];
  t.push("╔════════════════════════════════════════════════════════╗");
  t.push(`║  LINGUAFORGE · ${(result.langName||"").toUpperCase().padEnd(40)}║`);
  t.push("╚════════════════════════════════════════════════════════╝");
  t.push(`\nLanguage : ${result.langName}\nTagline  : ${result.tagline}\nBase     : ${rules?.baseLang||"English"}\nCreated  : ${new Date().toLocaleString()}\n`);
  t.push(line+"\nRULES APPLIED\n"+line);
  CATS.forEach(c=>{if(rules?.[c.key])t.push(`${c.label}: ${rules[c.key]}`);});
  t.push("\n"+line+"\nTRANSFORMATION\n"+line);
  result.transformationSteps?.forEach((s,i)=>{
    t.push(`\n[${i+1}] "${s.original}"`);
    s.steps?.forEach(st=>t.push(`    • ${st}`));
    t.push(`    → "${s.result}"`);
  });
  t.push("\n"+line+"\nLANGUAGE OVERVIEW\n"+line);
  t.push(g?.overview||"");
  t.push("\n"+line+"\nALPHABET KEY\n"+line);
  g?.alphabetKey?.forEach(r=>t.push(`  ${(r.base||"").padEnd(6)}→  ${(r.new||"").padEnd(12)} ${r.note||""}`));
  t.push("\n"+line+"\nVOCABULARY\n"+line);
  g?.vocabulary?.forEach(v=>t.push(`  ${(v.base||"").padEnd(14)}→  ${(v.new||"").padEnd(14)} [${v.pronunciation}]`));
  t.push("\n"+line+"\nPHRASES\n"+line);
  g?.phrases?.forEach(p=>t.push(`  "${p.meaning}" → ${p.new_lang}  [${p.pronunciation}]`));
  t.push("\n"+line+"\nWRITING SAMPLE\n"+line);
  t.push(g?.writingSample?.new_lang||"");
  t.push("\n— Translation —\n"+(g?.writingSample?.translation||""));
  t.push("\n"+line+"\nGenerated by LinguaForge — linguaforge.web.app\n"+line);
  return t.join("\n");
}

function makeScriptTranscript(glyphs, scriptName, rules) {
  const line = "─".repeat(58), t = [];
  t.push("╔════════════════════════════════════════════════════════╗");
  t.push(`║  LINGUAFORGE SCRIPT · ${(scriptName||"My Script").toUpperCase().padEnd(34)}║`);
  t.push("╚════════════════════════════════════════════════════════╝");
  t.push(`\nScript Name : ${scriptName||"My Script"}`);
  t.push(`Created     : ${new Date().toLocaleString()}`);
  t.push(`Glyphs Drawn: ${Object.keys(glyphs).length} / ${ALL_CHARS.length}\n`);

  // If language rules also present
  if (rules && Object.values(rules).some(v=>v)) {
    t.push(line+"\nLANGUAGE RULES ATTACHED\n"+line);
    CATS.forEach(c=>{ if(rules[c.key]) t.push(`${c.label}: ${rules[c.key]}`); });
    t.push("");
  }

  t.push(line+"\nALPHABET GLYPH MAP (A–Z)\n"+line);
  ALL_ALPHA.forEach(ch => {
    const has = !!glyphs[ch];
    t.push(`  ${ch}  ${has ? "[DRAWN — glyph stored as base64 PNG in vault]" : "[not drawn]"}`);
  });

  t.push("\n"+line+"\nNUMBER GLYPH MAP (0–9)\n"+line);
  NUM.forEach(ch => {
    const has = !!glyphs[ch];
    t.push(`  ${ch}  ${has ? "[DRAWN]" : "[not drawn]"}`);
  });

  t.push("\n"+line+"\nSYMBOL GLYPH MAP\n"+line);
  SYM.forEach(ch => {
    const has = !!glyphs[ch];
    t.push(`  "${ch}"  ${has ? "[DRAWN]" : "[not drawn]"}`);
  });

  t.push("\n"+line+"\nSCRIPT CIPHER KEY\n"+line);
  t.push("Each drawn glyph is a visual replacement for its base character.");
  t.push("The meaning is identical — only the written form changes.");
  t.push("\nExample encoding (using drawn glyphs):");
  const sample = "HELLO WORLD";
  t.push(`  Plain text : ${sample}`);
  t.push(`  In script  : ${sample.split("").map(c => glyphs[c] ? `[${c}]` : c).join("")}`);
  t.push("\n"+line+"\nGenerated by LinguaForge — linguaforge.web.app\n"+line);
  return t.join("\n");
}

function downloadTxt(filename, content) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([content],{type:"text/plain"}));
  a.download = filename; a.click(); URL.revokeObjectURL(a.href);
}

// ─── Smooth Draw Canvas ───────────────────────────────────────────
function DrawCanvas({ onSave, color="#d4a843", size=3, canvasSize=220 }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pts = useRef([]);       // raw point buffer for smoothing
  const history = useRef([]);
  const [hasMark, setHasMark] = useState(false);
  const T = useT();

  const getPos = (e) => {
    const c=ref.current, r=c.getBoundingClientRect();
    const sx=c.width/r.width, sy=c.height/r.height;
    const src=e.touches?e.touches[0]:e;
    return { x:(src.clientX-r.left)*sx, y:(src.clientY-r.top)*sy };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    pts.current = [getPos(e)];
    setHasMark(true);
  }, []);

  const doDraw = useCallback((e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const pos = getPos(e);
    pts.current.push(pos);
    // Only smooth-redraw from last snapshot
    if (pts.current.length >= 2) {
      const ctx = ref.current.getContext("2d");
      const last2 = pts.current.slice(-3);
      ctx.strokeStyle = color;
      ctx.lineWidth = size * (ref.current.width / canvasSize);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (last2.length === 3) {
        const mx = (last2[1].x + last2[2].x)/2;
        const my = (last2[1].y + last2[2].y)/2;
        ctx.beginPath();
        ctx.moveTo((last2[0].x+last2[1].x)/2, (last2[0].y+last2[1].y)/2);
        ctx.quadraticCurveTo(last2[1].x, last2[1].y, mx, my);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(last2[0].x, last2[0].y);
        ctx.lineTo(last2[1].x, last2[1].y);
        ctx.stroke();
      }
    }
  }, [color, size, canvasSize]);

  const endDraw = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    pts.current = [];
    history.current.push(ref.current.toDataURL());
  }, []);

  useEffect(() => {
    const c = ref.current;
    c.addEventListener("touchstart", startDraw, {passive:false});
    c.addEventListener("touchmove", doDraw, {passive:false});
    c.addEventListener("touchend", endDraw);
    return () => { c.removeEventListener("touchstart",startDraw); c.removeEventListener("touchmove",doDraw); c.removeEventListener("touchend",endDraw); };
  }, [startDraw, doDraw, endDraw]);

  const stampShape = (shapeId) => {
    const shape = SHAPES.find(s=>s.id===shapeId); if(!shape) return;
    const c = ref.current, ctx = c.getContext("2d"), sz = c.width;
    const svgContent = shape.svg(color, size*1.5);
    const fullSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="${sz}" height="${sz}">${svgContent}</svg>`;
    const url = URL.createObjectURL(new Blob([fullSvg],{type:"image/svg+xml"}));
    const img = new Image();
    img.onload = () => { ctx.drawImage(img,0,0,sz,sz); history.current.push(c.toDataURL()); setHasMark(true); URL.revokeObjectURL(url); };
    img.src = url;
  };

  // expose stampShape
  useEffect(() => { if(ref.current) ref.current._stamp = stampShape; }, [color, size]);

  const undo = () => {
    history.current.pop();
    const ctx = ref.current.getContext("2d");
    ctx.clearRect(0,0,ref.current.width,ref.current.height);
    if(history.current.length>0){const img=new Image();img.onload=()=>ctx.drawImage(img,0,0);img.src=history.current[history.current.length-1];}
    else setHasMark(false);
  };
  const clear = () => { const ctx=ref.current.getContext("2d"); ctx.clearRect(0,0,ref.current.width,ref.current.height); history.current=[]; setHasMark(false); };
  const save = () => { onSave(ref.current.toDataURL("image/png")); clear(); };

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"8px"}}>
      <div style={{position:"relative"}}>
        <svg style={{position:"absolute",inset:0,width:canvasSize,height:canvasSize,pointerEvents:"none",opacity:0.08}} viewBox={`0 0 ${canvasSize} ${canvasSize}`}>
          <line x1={canvasSize/2} y1={0} x2={canvasSize/2} y2={canvasSize} stroke={T.accent} strokeWidth="0.8" strokeDasharray="5,5"/>
          <line x1={0} y1={canvasSize/2} x2={canvasSize} y2={canvasSize/2} stroke={T.accent} strokeWidth="0.8" strokeDasharray="5,5"/>
          <rect x={canvasSize*0.08} y={canvasSize*0.08} width={canvasSize*0.84} height={canvasSize*0.84} stroke={T.accent} strokeWidth="0.8" fill="none"/>
        </svg>
        <canvas ref={ref} width={canvasSize*2} height={canvasSize*2}
          style={{width:canvasSize,height:canvasSize,background:T.canvas,borderRadius:"12px",border:`2px solid ${T.border}`,cursor:"crosshair",touchAction:"none",display:"block"}}
          onMouseDown={startDraw} onMouseMove={doDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
        />
      </div>
      <div style={{display:"flex",gap:"6px"}}>
        <TBtn onClick={undo}>↩ Undo</TBtn>
        <TBtn onClick={clear}>✕ Clear</TBtn>
        <TBtn onClick={save} style={hasMark?{background:T.accentDim,color:T.accent,borderColor:T.accent,opacity:1}:{opacity:0.4}} disabled={!hasMark}>✓ Assign</TBtn>
      </div>
    </div>
  );
}

// ─── Shared tiny components ───────────────────────────────────────
function TBtn({children,onClick,style={},disabled=false,className=""}) {
  const T=useT();
  return <button disabled={disabled} onClick={onClick} className={className}
    style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"8px",color:T.textDim,padding:"5px 11px",cursor:disabled?"not-allowed":"pointer",fontFamily:"inherit",fontSize:"0.8rem",transition:"all 0.15s",...style}}>{children}</button>;
}
function TTab({label,active,onClick}) {
  const T=useT();
  return <button onClick={onClick} style={{background:active?T.accentDim:T.card,border:`1px solid ${active?T.accent:T.border}`,borderRadius:"8px",color:active?T.accent:T.textDim,padding:"5px 13px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.82rem",transition:"all 0.15s"}}>{label}</button>;
}
function TInput({value,onChange,placeholder,style={}}) {
  const T=useT();
  return <input value={value} onChange={onChange} placeholder={placeholder}
    style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:"8px",color:T.text,padding:"8px 12px",fontFamily:"inherit",fontSize:"0.9rem",boxSizing:"border-box",outline:"none",...style}}/>;
}
function TTextarea({value,onChange,placeholder,rows=4}) {
  const T=useT();
  return <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{width:"100%",background:T.inputBg,border:`1px solid ${T.border}`,borderRadius:"8px",color:T.text,padding:"8px 12px",fontFamily:"inherit",fontSize:"0.88rem",resize:"vertical",boxSizing:"border-box",lineHeight:1.6,outline:"none"}}/>;
}
function Card({children,style={}}) {
  const T=useT();
  return <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"16px",padding:"20px",marginBottom:"16px",...style}}>{children}</div>;
}

// ─── Glyph Keyboard ───────────────────────────────────────────────
function GlyphKb({glyphs,layout,onKey,onSpace,onBack}) {
  const T=useT();
  const rows=layout==="qwerty"?QWERTY:ABC;
  const K=({ch})=>(
    <button onClick={()=>onKey(ch)} title={ch} style={{width:"36px",height:"36px",background:T.card,border:`1px solid ${T.border}`,borderRadius:"7px",cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:"2px",overflow:"hidden",transition:"all 0.1s"}} className="gk">
      {glyphs[ch]?<img src={glyphs[ch]} alt="?" style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:"0.72rem",color:T.textDim,fontWeight:700}}>{ch}</span>}
    </button>
  );
  return (
    <div style={{background:T.canvas,border:`1px solid ${T.border}`,borderRadius:"12px",padding:"10px",display:"flex",flexDirection:"column",gap:"5px"}}>
      {rows.map((row,i)=><div key={i} style={{display:"flex",gap:"3px",justifyContent:"center"}}>{row.map(ch=><K key={ch} ch={ch}/>)}</div>)}
      <div style={{display:"flex",gap:"3px",justifyContent:"center",flexWrap:"wrap"}}>
        {NUM.map(ch=><K key={ch} ch={ch}/>)}
      </div>
      <div style={{display:"flex",gap:"3px",justifyContent:"center"}}>
        <button style={{minWidth:"80px",height:"36px",background:T.card,border:`1px solid ${T.border}`,borderRadius:"7px",cursor:"pointer",fontSize:"0.68rem",color:T.textDim}} onClick={onSpace}>SPACE</button>
        <button style={{minWidth:"52px",height:"36px",background:T.card,border:"1px solid #c05050",borderRadius:"7px",cursor:"pointer",fontSize:"0.68rem",color:"#e05050"}} onClick={onBack}>⌫ Back</button>
      </div>
    </div>
  );
}

// ─── Practice Item ────────────────────────────────────────────────
function PracticeItem({s, glyphs, kb}) {
  const T=useT();
  const [ans,setAns]=useState("");
  const [checked,setChecked]=useState(false);
  const [hint,setHint]=useState(false);
  const [rev,setRev]=useState(false);
  const [useKb,setUseKb]=useState(true);
  const correct = ans.trim().toUpperCase()===s.text.trim().toUpperCase();

  const GLine=({text})=>(
    <div style={{display:"flex",flexWrap:"wrap",gap:"3px",alignItems:"center"}}>
      {text.split("").map((c,i)=>{
        if(c===" ")return <span key={i} style={{width:"12px"}}/>;
        return glyphs[c]
          ?<img key={i} src={glyphs[c]} alt="?" style={{width:"32px",height:"32px",objectFit:"contain"}}/>
          :<span key={i} style={{width:"20px",height:"32px",display:"inline-flex",alignItems:"center",justifyContent:"center",color:T.accentDim,fontSize:"0.75rem",border:`1px dashed ${T.border}`,borderRadius:"4px"}}>?</span>;
      })}
    </div>
  );

  const onKey=(ch)=>setAns(a=>a+ch);
  const onSpace=()=>setAns(a=>a+" ");
  const onBack=()=>setAns(a=>a.slice(0,-1));

  return (
    <div style={{background:T.card,border:`1px solid ${checked?(correct?T.greenBorder:"#c05050"):T.border}`,borderRadius:"14px",padding:"16px",transition:"border-color 0.3s"}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"8px",alignItems:"center"}}>
        <span style={{color:T.textDim,fontSize:"0.7rem",letterSpacing:"0.1em"}}>SENTENCE {s.id} · READ GLYPHS → TYPE BACK</span>
        <span style={{fontSize:"0.68rem",padding:"2px 8px",borderRadius:"20px",border:`1px solid ${T.border}`,color:T.textDim}}>{s.difficulty}</span>
      </div>
      <div style={{background:T.canvas,borderRadius:"10px",padding:"12px",marginBottom:"8px",border:`1px solid ${T.border}`,minHeight:"52px"}}><GLine text={s.text}/></div>
      <div style={{fontSize:"0.75rem",color:T.textDim,marginBottom:"10px"}}>{s.text.replace(/ /g,"").length} letters · {s.text.split(" ").length} words</div>
      <div style={{background:T.canvas,borderRadius:"8px",padding:"8px 12px",minHeight:"44px",border:`1px solid ${T.border}`,marginBottom:"8px",fontSize:"0.95rem",color:T.text}}>
        {ans||<span style={{color:T.textMuted,fontStyle:"italic",fontSize:"0.82rem"}}>Type using keyboard below…</span>}
        {ans&&<span style={{animation:"blink 1s step-end infinite",color:T.accent}}>|</span>}
      </div>
      <div style={{display:"flex",gap:"6px",marginBottom:"8px",flexWrap:"wrap"}}>
        <TBtn onClick={()=>setUseKb(true)} style={useKb?{background:T.accentDim,color:T.accent,borderColor:T.accent}:{}}>✍️ Glyph Keys</TBtn>
        <TBtn onClick={()=>setUseKb(false)} style={!useKb?{background:T.accentDim,color:T.accent,borderColor:T.accent}:{}}>⌨️ Type</TBtn>
        <TBtn onClick={onBack}>⌫</TBtn>
        <TBtn onClick={()=>setAns("")}>✕</TBtn>
      </div>
      {useKb?<GlyphKb glyphs={glyphs} layout={kb} onKey={onKey} onSpace={onSpace} onBack={onBack}/>
        :<TTextarea value={ans} onChange={e=>setAns(e.target.value.toUpperCase())} placeholder="Type the letters…" rows={2}/>}
      <div style={{display:"flex",gap:"6px",marginTop:"10px",flexWrap:"wrap"}}>
        <button style={{background:T.forgeGrad,border:`1px solid ${T.accent}`,borderRadius:"8px",color:"#fff",padding:"6px 14px",cursor:!ans.trim()?"not-allowed":"pointer",fontFamily:"inherit",fontSize:"0.85rem",fontWeight:700,opacity:!ans.trim()?0.5:1}} onClick={()=>setChecked(true)} disabled={!ans.trim()}>✓ Check</button>
        <TBtn onClick={()=>setHint(h=>!h)}>💡 Hint</TBtn>
        <TBtn onClick={()=>setRev(a=>!a)}>👁 Answer</TBtn>
      </div>
      {hint&&<div style={{background:T.surface,border:`1px solid ${T.accentDim}`,borderRadius:"8px",padding:"8px 12px",marginTop:"8px",fontSize:"0.84rem",color:T.textDim}}>💡 {s.hint}</div>}
      {rev&&<div style={{background:T.greenDark,border:`1px solid ${T.greenBorder}`,borderRadius:"8px",padding:"8px 12px",marginTop:"8px",fontSize:"0.9rem",color:T.green,fontWeight:700}}>✅ {s.text}</div>}
      {checked&&<div style={{marginTop:"8px",padding:"8px 12px",borderRadius:"8px",background:correct?T.greenDark:T.surface,border:`1px solid ${correct?T.greenBorder:"#c05050"}`,color:correct?T.green:"#e05050",fontSize:"0.88rem"}}>
        {correct?"🎉 Perfect! Every glyph read correctly.":"❌ Not quite — click Answer to see what the glyphs said."}
      </div>}
    </div>
  );
}

// ─── Name Dialog ─────────────────────────────────────────────────
function NameDialog({ title, defaultValue, placeholder, onConfirm, onCancel }) {
  const T = useT();
  const [val, setVal] = useState(defaultValue || "");
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 60); }, []);

  const confirm = () => { if (val.trim()) onConfirm(val.trim()); };
  const onKey = (e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") onCancel(); };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <div style={{ background:T.surface, border:`1px solid ${T.accentDim}`, borderRadius:"16px", padding:"28px 28px 22px", minWidth:"320px", maxWidth:"420px", width:"90%", boxShadow:"0 8px 48px rgba(0,0,0,0.5)", animation:"fadeUp 0.2s ease" }}>
        <div style={{ fontSize:"1rem", fontWeight:700, color:T.accent, marginBottom:"6px" }}>{title}</div>
        <div style={{ fontSize:"0.84rem", color:T.textDim, marginBottom:"16px" }}>Give it a name so you can find it later.</div>
        <input
          ref={inputRef}
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
          style={{ width:"100%", background:T.inputBg, border:`1.5px solid ${T.accent}`, borderRadius:"9px", color:T.text, padding:"10px 14px", fontFamily:"inherit", fontSize:"1rem", boxSizing:"border-box", outline:"none", marginBottom:"16px" }}
        />
        <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end" }}>
          <button onClick={onCancel} style={{ background:"transparent", border:`1px solid ${T.border}`, borderRadius:"8px", color:T.textDim, padding:"8px 18px", cursor:"pointer", fontFamily:"inherit", fontSize:"0.88rem" }}>Cancel</button>
          <button onClick={confirm} disabled={!val.trim()} style={{ background:T.forgeGrad, border:`1px solid ${T.accent}`, borderRadius:"8px", color:"#fff", padding:"8px 22px", cursor:val.trim()?"pointer":"not-allowed", fontFamily:"inherit", fontSize:"0.88rem", fontWeight:700, opacity:val.trim()?1:0.5 }}>
            💾 Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────
function HomePage({onStart,T}) {
  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.text,overflowX:"hidden"}}>
      {/* Hero */}
      <div style={{textAlign:"center",padding:"80px 20px 60px",position:"relative",overflow:"hidden"}}>
        {/* bg glow */}
        <div style={{position:"absolute",top:"-20%",left:"50%",transform:"translateX(-50%)",width:"600px",height:"600px",background:`radial-gradient(circle,${T.accentSoft} 0%,transparent 70%)`,pointerEvents:"none"}}/>
        <div style={{fontSize:"13px",letterSpacing:"8px",color:T.accentDim,marginBottom:"20px",textTransform:"uppercase",position:"relative"}}>ᚠ ᚢ ᚦ ᚨ ᚱ ᚲ ᚷ ᚹ ᚺ ᚾ</div>
        <h1 style={{fontSize:"clamp(3rem,8vw,5.5rem)",fontWeight:900,letterSpacing:"0.15em",lineHeight:1,marginBottom:"16px",position:"relative"}}>
          <span style={{color:T.accent}}>LINGUA</span>FORGE
        </h1>
        <p style={{fontSize:"clamp(1rem,2.5vw,1.35rem)",color:T.textDim,fontStyle:"italic",marginBottom:"40px",maxWidth:"500px",margin:"0 auto 40px",lineHeight:1.6}}>
          Craft a language no tongue has ever spoken.<br/>Draw its alphabet. Make it yours.
        </p>
        <div style={{display:"flex",gap:"12px",justifyContent:"center",flexWrap:"wrap",marginBottom:"60px"}}>
          <button onClick={onStart} style={{background:T.forgeGrad,border:`1px solid ${T.accent}`,borderRadius:"12px",color:"#fff",padding:"14px 36px",fontSize:"1.05rem",fontWeight:700,letterSpacing:"0.1em",cursor:"pointer",fontFamily:"inherit",boxShadow:T.shadow}} className="hero-btn">
            ⚒️ Start Forging Free
          </button>
          <button onClick={()=>onStart("script")} style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:"12px",color:T.text,padding:"14px 28px",fontSize:"1.05rem",cursor:"pointer",fontFamily:"inherit"}}>
            ✍️ Draw My Script
          </button>
        </div>
        <div style={{fontSize:"0.78rem",color:T.textMuted}}>✓ No login &nbsp;·&nbsp; ✓ Free forever &nbsp;·&nbsp; ✓ AI-powered &nbsp;·&nbsp; ✓ Saves in your browser</div>
      </div>

      {/* Features */}
      <div style={{maxWidth:"900px",margin:"0 auto",padding:"0 20px 80px"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:"16px",marginBottom:"60px"}}>
          {[
            {icon:"⚒️",title:"Language Forge",desc:"Set phonology, grammar and vocabulary rules. AI builds a complete language with alphabet key, pronunciation guide and sample sentences."},
            {icon:"✍️",title:"Script Builder",desc:"Draw custom glyphs for each letter using your mouse or finger. Stamp 2D shapes as starting points. Build a writing system from scratch."},
            {icon:"🎯",title:"Practice Mode",desc:"AI generates reading sentences shown in your custom glyphs. Use the custom keyboard to type them back. Hints and answers included."},
            {icon:"🗄️",title:"Personal Vault",desc:"Every language and script you create saves automatically in your browser. Load, download, or copy any creation any time."},
          ].map((f,i)=>(
            <div key={i} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"16px",padding:"24px",transition:"transform 0.2s,border-color 0.2s"}} className="feat-card">
              <div style={{fontSize:"2rem",marginBottom:"12px"}}>{f.icon}</div>
              <div style={{fontWeight:700,fontSize:"1.05rem",color:T.accent,marginBottom:"8px"}}>{f.title}</div>
              <div style={{color:T.textDim,fontSize:"0.88rem",lineHeight:1.7}}>{f.desc}</div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div style={{textAlign:"center",marginBottom:"40px"}}>
          <h2 style={{fontSize:"1.8rem",fontWeight:900,color:T.text,letterSpacing:"0.05em",marginBottom:"8px"}}>How It Works</h2>
          <p style={{color:T.textDim,fontSize:"0.9rem"}}>Three steps to your own language</p>
        </div>
        <div style={{display:"flex",gap:"0",marginBottom:"60px",position:"relative"}}>
          <div style={{position:"absolute",top:"32px",left:"16.66%",right:"16.66%",height:"2px",background:`linear-gradient(to right,${T.accentDim},${T.accentDim})`,zIndex:0}}/>
          {[
            {n:"1",title:"Set the Rules",desc:"Choose your base language and define phonology, grammar and vocabulary transformation rules."},
            {n:"2",title:"Forge & Draw",desc:"AI generates your full language. Draw custom glyphs for each letter to make a unique writing system."},
            {n:"3",title:"Practice & Save",desc:"Translate AI sentences using your script. Download the full transcript to keep forever."},
          ].map((s,i)=>(
            <div key={i} style={{flex:1,textAlign:"center",padding:"0 16px",position:"relative",zIndex:1}}>
              <div style={{width:"56px",height:"56px",borderRadius:"50%",background:T.forgeGrad,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:"1.3rem",fontWeight:900,color:"#fff"}}>{s.n}</div>
              <div style={{fontWeight:700,fontSize:"0.95rem",color:T.text,marginBottom:"6px"}}>{s.title}</div>
              <div style={{color:T.textDim,fontSize:"0.82rem",lineHeight:1.6}}>{s.desc}</div>
            </div>
          ))}
        </div>

        {/* Sample languages */}
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:"20px",padding:"28px",marginBottom:"48px"}}>
          <div style={{fontWeight:700,fontSize:"1rem",color:T.accent,marginBottom:"16px",letterSpacing:"0.05em"}}>🌍 Languages created with LinguaForge</div>
          <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
            {[
              {name:"Aethran",base:"English",rules:"th→vr, vowels doubled",tag:"Fantasy"},
              {name:"Solketh",base:"Spanish",rules:"s→zh, verb-first syntax",tag:"Sci-Fi"},
              {name:"Vroryn",base:"English",rules:"drop final consonants, prefix vel-",tag:"Elvish"},
              {name:"Zokhari",base:"Arabic",rules:"vowel shift +2, add -okh plurals",tag:"Ancient"},
            ].map((l,i)=>(
              <div key={i} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"10px",padding:"12px 16px",flex:"1 1 160px"}}>
                <div style={{color:T.accent,fontWeight:700,fontSize:"0.95rem"}}>{l.name}</div>
                <div style={{color:T.textDim,fontSize:"0.75rem",marginTop:"2px"}}>from {l.base} · {l.rules}</div>
                <div style={{marginTop:"6px",display:"inline-block",fontSize:"0.68rem",padding:"2px 8px",borderRadius:"20px",background:T.accentSoft,color:T.accent,border:`1px solid ${T.accentDim}`}}>{l.tag}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{textAlign:"center",padding:"40px",background:T.surface,border:`1px solid ${T.accentDim}`,borderRadius:"20px",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:"-40%",left:"50%",transform:"translateX(-50%)",width:"400px",height:"300px",background:`radial-gradient(ellipse,${T.accentSoft} 0%,transparent 70%)`,pointerEvents:"none"}}/>
          <h2 style={{fontSize:"1.8rem",fontWeight:900,color:T.accent,marginBottom:"12px",position:"relative"}}>Ready to forge your language?</h2>
          <p style={{color:T.textDim,marginBottom:"24px",position:"relative"}}>Free. No account needed. Starts in seconds.</p>
          <button onClick={onStart} style={{background:T.forgeGrad,border:`1px solid ${T.accent}`,borderRadius:"12px",color:"#fff",padding:"14px 40px",fontSize:"1.05rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",position:"relative"}} className="hero-btn">
            ⚒️ Forge My Language
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────
export default function App() {
  const [isDark, setIsDark] = useState(true);
  const T = isDark ? DARK : LIGHT;
  const [page, setPage] = useState("home"); // home | forge | script | practice

  // Forge state
  const [rules, setRules] = useState({phonology:"",orthography:"",morphology:"",syntax:"",lexical:"",baseLang:"English",langNameHint:""});
  const [activeTab, setActiveTab] = useState("phonology");
  const [forgeLoading, setForgeLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [forgeError, setForgeError] = useState("");
  const [activeSection, setActiveSection] = useState(0);
  const [saveMsg, setSaveMsg] = useState("");
  const [copyMsg, setCopyMsg] = useState("");

  // Name dialogs
  const [langDialog, setLangDialog] = useState(false);   // show before saving language
  const [scriptDialog, setScriptDialog] = useState(false); // show before saving script

  // Vault state
  const [vaultOpen, setVaultOpen] = useState(false);
  const [savedLangs, setSavedLangs] = useState([]);

  // Script state
  const [glyphs, setGlyphs] = useState({});
  const [selChar, setSelChar] = useState("A");
  const [charSet, setCharSet] = useState("alpha");
  const [brushSize, setBrushSize] = useState(3);
  const [brushColor, setBrushColor] = useState("#d4a843");
  const [scriptName, setScriptName] = useState("My Script");
  const [scriptMsg, setScriptMsg] = useState("");
  const [selShape, setSelShape] = useState(null);

  // Practice state
  const [practiceLevel, setPracticeLevel] = useState("beginner");
  const [kbLayout, setKbLayout] = useState("qwerty");
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [sentences, setSentences] = useState([]);
  const [practiceError, setPracticeError] = useState("");

  const resultRef = useRef(null);

  // Auto-save glyphs
  useEffect(() => {
    if(Object.keys(glyphs).length===0) return;
    window.storage?.set("autosave:glyphs",JSON.stringify({glyphs,scriptName,ts:Date.now()})).catch(()=>{});
  },[glyphs,scriptName]);

  useEffect(() => {
    loadSaved();
    window.storage?.get("autosave:glyphs").then(r=>{
      if(!r) return;
      try{ const d=JSON.parse(r.value); if(d.glyphs&&Object.keys(d.glyphs).length>0){setGlyphs(d.glyphs);if(d.scriptName)setScriptName(d.scriptName);}}catch{}
    }).catch(()=>{});
  },[]);

  const loadSaved = async () => {
    try {
      const keys = await window.storage?.list("lang:");
      const items = [];
      for(const k of (keys?.keys||[])){try{const r=await window.storage.get(k);if(r)items.push({key:k,...JSON.parse(r.value)});}catch{}}
      items.sort((a,b)=>(b.savedAt||0)-(a.savedAt||0));
      setSavedLangs(items);
    } catch {}
  };

  const updateRule = (k,v) => setRules(r=>({...r,[k]:v}));

  // ── Forge ─────────────────────────────────────────────────────
  const forgePrompt = () => {
    const rl = CATS.map(c=>`- ${c.label}: ${rules[c.key]||"(use creative defaults)"}`).join("\n");
    return `You are a Constructed Language Architect. Create a language called ${rules.langNameHint?`"${rules.langNameHint}" or inspired by it`:"a fitting invented name"} transforming ${rules.baseLang||"English"}:\n\n${rl}\n\nReturn ONLY pure JSON:\n{"langName":"...","tagline":"...","transformationSteps":[{"original":"Hello, how are you?","steps":["step..."],"result":"..."},{"original":"The sun rises.","steps":["step..."],"result":"..."},{"original":"I go to market tomorrow.","steps":["step..."],"result":"..."}],"guide":{"overview":"2 paragraphs about origin and feel","alphabetKey":[{"base":"a","new":"symbol","note":""}],"pronunciationGuide":[{"symbol":"x","sounds_like":"...","example":"..."}],"grammarRules":[{"rule":"...","description":"...","example":"..."}],"vocabulary":[{"base":"hello","new":"...","pronunciation":"..."}],"phrases":[{"meaning":"Hello!","new_lang":"...","pronunciation":"..."}],"exercises":[{"prompt":"Translate: The cat sleeps.","answer":"..."}],"writingSample":{"new_lang":"...","translation":"..."}}}`;
  };

  const runForge = async () => {
    setForgeLoading(true); setForgeError(""); setResult(null);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type": "application/json",
"x-api-key": "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",
"anthropic-version": "2023-06-01",
"anthropic-dangerous-direct-browser-access": "true"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:4000,messages:[{role:"user",content:forgePrompt()}]})});
      const data = await res.json();
      const parsed = JSON.parse((data.content?.map(b=>b.text||"").join("")||"").replace(/```json|```/g,"").trim());
      setResult(parsed);
      setTimeout(()=>resultRef.current?.scrollIntoView({behavior:"smooth"}),100);
    } catch { setForgeError("Generation failed — please try again."); }
    setForgeLoading(false);
  };

  // ── Save ──────────────────────────────────────────────────────
  // Show name dialog first, then actually save
  const handleSave = () => { if (!result) return; setLangDialog(true); };

  const doSaveLang = async (customName) => {
    setLangDialog(false);
    try {
      const payload = {
        langName: customName || result.langName,
        tagline: result.tagline, rules,
        result: { ...result, langName: customName || result.langName },
        glyphs, scriptName, glyphCount: Object.keys(glyphs).length, savedAt: Date.now(),
      };
      await window.storage?.set(`lang:${Date.now()}`, JSON.stringify(payload));
      setSaveMsg(`✅ "${customName}" saved to vault!`);
      setTimeout(() => setSaveMsg(""), 3500);
      await loadSaved();
      setVaultOpen(true);
    } catch { setSaveMsg("❌ Save failed"); setTimeout(() => setSaveMsg(""), 3000); }
  };

  const handleDownload = () => {
    if(!result) return;
    downloadTxt(`${result.langName.replace(/\s+/g,"_")}_LinguaForge.txt`, makeTranscript(result,rules));
  };

  const handleCopy = () => {
    if(!result) return;
    navigator.clipboard.writeText(makeTranscript(result,rules)).then(()=>{setCopyMsg("Copied!");setTimeout(()=>setCopyMsg(""),2500);});
  };

  const loadVault = (item) => {
    if(item.result)setResult(item.result); if(item.rules)setRules(item.rules);
    if(item.glyphs&&Object.keys(item.glyphs).length>0){setGlyphs(item.glyphs);setScriptName(item.scriptName||"My Script");}
    setVaultOpen(false); setPage("forge");
    setTimeout(()=>resultRef.current?.scrollIntoView({behavior:"smooth"}),200);
  };

  const delSaved = async (k) => { try{await window.storage?.delete(k);loadSaved();}catch{} };

  // Guide sections
  const td = {padding:"7px 10px",borderBottom:`1px solid ${T.border}`,color:T.text,fontSize:"0.86rem"};
  const th = {padding:"7px 10px",background:T.accentDim,color:T.accent,fontSize:"0.7rem",letterSpacing:"0.1em",textTransform:"uppercase",textAlign:"left"};
  const GSEC = result ? [
    {t:"Overview",c:<div style={{lineHeight:1.8,color:T.text}}>{result.guide?.overview?.split("\n").map((p,i)=><p key={i} style={{marginBottom:"0.5rem"}}>{p}</p>)}</div>},
    {t:"Alphabet",c:<div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.86rem"}}><thead><tr><th style={th}>Base</th><th style={th}>New</th><th style={th}>Note</th></tr></thead><tbody>{(result.guide?.alphabetKey||[]).map((r,i)=><tr key={i} style={i%2?{background:"rgba(128,100,0,0.04)"}:{}}><td style={td}>{r.base}</td><td style={{...td,color:T.accent,fontWeight:700}}>{r.new}</td><td style={td}>{r.note||"—"}</td></tr>)}</tbody></table></div>},
    {t:"Pronunciation",c:<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:"8px"}}>{result.guide?.pronunciationGuide?.map((p,i)=><div key={i} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"10px",textAlign:"center"}}><div style={{color:T.accent,fontSize:"1.1rem",fontWeight:700}}>{p.symbol}</div><div style={{color:T.textDim,fontSize:"0.72rem",margin:"3px 0"}}>{p.sounds_like}</div><div style={{color:T.purpleL,fontSize:"0.72rem",fontStyle:"italic"}}>{p.example}</div></div>)}</div>},
    {t:"Grammar",c:<div style={{display:"flex",flexDirection:"column",gap:"8px"}}>{result.guide?.grammarRules?.map((g,i)=><div key={i} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"12px"}}><div style={{color:T.accent,fontWeight:700,marginBottom:"4px"}}>{g.rule}</div><div style={{color:T.text,fontSize:"0.86rem",lineHeight:1.6}}>{g.description}</div><div style={{color:T.purpleL,fontSize:"0.8rem",fontStyle:"italic",marginTop:"4px"}}>e.g. {g.example}</div></div>)}</div>},
    {t:"Vocab",c:<div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr>{["Base","New","Pronunciation"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead><tbody>{(result.guide?.vocabulary||[]).map((v,i)=><tr key={i} style={i%2?{background:"rgba(128,100,0,0.04)"}:{}}><td style={td}>{v.base}</td><td style={{...td,color:T.accent,fontWeight:700}}>{v.new}</td><td style={td}>{v.pronunciation}</td></tr>)}</tbody></table></div>},
    {t:"Phrases",c:<div style={{display:"flex",flexDirection:"column",gap:"6px"}}>{result.guide?.phrases?.map((p,i)=><div key={i} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"8px",padding:"9px 12px",display:"flex",flexWrap:"wrap",gap:"8px",alignItems:"center"}}><span style={{color:T.textDim,minWidth:"100px",fontSize:"0.84rem"}}>{p.meaning}</span><span style={{color:T.accent,fontWeight:700,flex:1}}>{p.new_lang}</span><span style={{color:T.purpleL,fontSize:"0.78rem",fontStyle:"italic"}}>[{p.pronunciation}]</span></div>)}</div>},
    {t:"Sample",c:<div><div style={{background:T.card,border:`1px solid ${T.accentDim}`,borderRadius:"10px",padding:"16px",marginBottom:"10px"}}><div style={{fontSize:"0.68rem",letterSpacing:"0.1em",textTransform:"uppercase",color:T.accentDim,marginBottom:"8px"}}>In {result.langName}</div><p style={{color:T.accent,fontStyle:"italic",lineHeight:1.8}}>{result.guide?.writingSample?.new_lang}</p></div><p style={{color:T.textDim,lineHeight:1.8,fontSize:"0.88rem"}}>{result.guide?.writingSample?.translation}</p></div>},
  ]:[];

  // ── Script ────────────────────────────────────────────────────
  // After assigning a glyph, auto-advance to next character in current set
  const advanceChar = useCallback((current) => {
    const group = charGroups_ref.current[charSet] || ALL_ALPHA;
    const idx = group.indexOf(current);
    if (idx >= 0 && idx < group.length - 1) setSelChar(group[idx + 1]);
  }, [charSet]);

  const charGroups = {alpha:ALL_ALPHA,num:NUM,sym:SYM};
  const charGroups_ref = useRef(charGroups);
  useEffect(() => { charGroups_ref.current = {alpha:ALL_ALPHA,num:NUM,sym:SYM}; }, []);

  const saveGlyph = (dataUrl) => {
    setGlyphs(g => ({ ...g, [selChar]: dataUrl }));
    setScriptMsg(`✅ "${selChar}" saved! → Press Enter or draw next`);
    setTimeout(() => setScriptMsg(""), 1800);
    // Auto-advance to next character
    advanceChar(selChar);
  };
  const clearGlyph = (ch) => setGlyphs(g=>{const n={...g};delete n[ch];return n;});

  const handleStamp = (shapeId) => {
    setSelShape(shapeId);
    const domCanvas = document.getElementById("draw-canvas");
    if(domCanvas?._stamp) domCanvas._stamp(shapeId);
  };

  const saveScript = () => { setScriptDialog(true); };

  const doSaveScript = async (customName) => {
    setScriptDialog(false);
    const name = customName || scriptName;
    setScriptName(name);
    try {
      await window.storage?.set("script:latest", JSON.stringify({ glyphs, scriptName: name, savedAt: Date.now() }));
      setScriptMsg(`✅ Script "${name}" saved!`);
      setTimeout(() => setScriptMsg(""), 3000);
    } catch { setScriptMsg("❌ Failed"); setTimeout(() => setScriptMsg(""), 2000); }
  };

  // Enter key = advance to next character when on script page
  useEffect(() => {
    const handler = (e) => {
      if (page !== "script") return;
      if (["INPUT","TEXTAREA","BUTTON"].includes(e.target.tagName)) return;
      if (e.key === "Enter") { advanceChar(selChar); }
      if (e.key === "ArrowRight" || e.key === "Tab") { e.preventDefault(); advanceChar(selChar); }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        const group = charGroups_ref.current[charSet] || ALL_ALPHA;
        const idx = group.indexOf(selChar);
        if (idx > 0) setSelChar(group[idx - 1]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [page, selChar, charSet, advanceChar]);

  const assignedCount = Object.keys(glyphs).length;

  // ── Practice ──────────────────────────────────────────────────
  const practicePrompt = () => {
    const lvl = {beginner:"3-5 word simple sentences",intermediate:"6-9 word sentences",advanced:"10-14 word sentences"};
    return `Generate 6 ${lvl[practiceLevel]} in ${rules.baseLang||"English"} for a glyph-reading exercise.\n\nReturn ONLY pure JSON:\n{"sentences":[{"id":1,"text":"THE CAT SITS","hint":"3 words — an animal, a verb, a location","difficulty":"easy"},...]}\n\nRules: text must be UPPERCASE letters A-Z and spaces only. Vary difficulty.`;
  };

  const runPractice = async () => {
    setPracticeLoading(true); setPracticeError(""); setSentences([]);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1500,messages:[{role:"user",content:practicePrompt()}]})});
      const data = await res.json();
      const parsed = JSON.parse((data.content?.map(b=>b.text||"").join("")||"").replace(/```json|```/g,"").trim());
      setSentences((parsed.sentences||[]).map(s=>({...s,text:(s.text||"").toUpperCase()})));
    } catch { setPracticeError("Failed — please try again."); }
    setPracticeLoading(false);
  };

  // ── Layout helpers ────────────────────────────────────────────
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Lato:ital,wght@0,300;0,400;0,700;1,400&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Lato',sans-serif}
    h1,h2,h3,.brand{font-family:'Cinzel',serif}
    textarea:focus,input:focus,select:focus{outline:1px solid ${T.accentDim}!important}
    .hero-btn:hover{transform:translateY(-2px);filter:brightness(1.1);box-shadow:${T.shadow}}
    .feat-card:hover{transform:translateY(-4px);border-color:${T.accentDim}!important}
    .forge-btn:hover:not(:disabled){filter:brightness(1.15);box-shadow:${T.shadow};transform:translateY(-1px)}
    .action-btn:hover{transform:translateY(-2px);filter:brightness(1.1)}
    .gk:hover{border-color:${T.accent}!important;transform:scale(1.1)}
    .shape-btn:hover,.shape-btn.on{border-color:${T.accent}!important;background:${T.accentDim}!important}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    .forge-spinner{width:15px;height:15px;border:2px solid ${T.accentDim};border-top-color:${T.accent};border-radius:50%;animation:spin 0.8s linear infinite;display:inline-block}
    ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:${T.bg}}::-webkit-scrollbar-thumb{background:${T.accentDim};border-radius:3px}
    select option{background:${T.card};color:${T.text}}
    .anim{animation:fadeUp 0.4s ease both}
  `;

  const NavBar = () => (
    <nav style={{background:T.surface,borderBottom:`1px solid ${T.border}`,position:"sticky",top:0,zIndex:200,boxShadow:T.shadow}}>
      <div style={{maxWidth:"1000px",margin:"0 auto",padding:"10px 16px",display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
        <span className="brand" onClick={()=>setPage("home")} style={{fontWeight:900,fontSize:"1.1rem",letterSpacing:"0.12em",cursor:"pointer",flexShrink:0,color:T.text}}>
          <span style={{color:T.accent}}>LINGUA</span>FORGE
        </span>
        <div style={{display:"flex",gap:"4px",flex:1,flexWrap:"wrap"}}>
          {[["home","🏠 Home"],["forge","⚒️ Forge"],["script","✍️ Script"],["practice","🎯 Practice"]].map(([p,l])=>(
            <TTab key={p} label={l} active={page===p} onClick={()=>setPage(p)}/>
          ))}
        </div>
        <button onClick={()=>setIsDark(d=>!d)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"20px",padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.82rem",color:T.text}}>
          {isDark?"☀️ Light":"🌙 Dark"}
        </button>
        <button onClick={()=>setVaultOpen(v=>!v)} style={{background:"transparent",border:`1px solid ${T.accentDim}`,borderRadius:"8px",color:T.accentDim,padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.82rem"}}>
          🗄️ Vault{savedLangs.length>0&&<span style={{background:T.accent,color:T.isDark?"#000":"#fff",borderRadius:"50%",padding:"0 5px",fontSize:"0.62rem",fontWeight:700,marginLeft:"5px"}}>{savedLangs.length}</span>}
        </button>
      </div>
    </nav>
  );

  const VaultDrawer = () => (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,maxWidth:"1000px",margin:"0 auto",padding:"16px 20px",borderTop:"none"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
        <span style={{color:T.accent,fontWeight:700,fontSize:"1rem"}}>🗄️ Your Saved Languages</span>
        <TBtn onClick={()=>setVaultOpen(false)}>✕ Close</TBtn>
      </div>
      {savedLangs.length===0
        ?<p style={{color:T.textDim,fontStyle:"italic",fontSize:"0.88rem"}}>Nothing saved yet — forge a language and save it!</p>
        :<div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
          {savedLangs.map(item=>(
            <div key={item.key} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"12px",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"12px",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:0}}>
                {/* User-assigned name — big and prominent */}
                <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap",marginBottom:"3px"}}>
                  <span style={{color:T.accent,fontWeight:900,fontSize:"1.05rem",letterSpacing:"0.04em"}}>{item.langName}</span>
                  <span style={{fontSize:"0.65rem",padding:"2px 8px",borderRadius:"20px",background:T.accentSoft,border:`1px solid ${T.accentDim}`,color:T.accent}}>Language</span>
                  {item.glyphCount>0&&<span style={{fontSize:"0.65rem",padding:"2px 8px",borderRadius:"20px",background:T.greenDark,border:`1px solid ${T.greenBorder}`,color:T.green}}>✍️ {item.glyphCount} glyphs · {item.scriptName||"Script"}</span>}
                </div>
                <div style={{color:T.textDim,fontSize:"0.78rem"}}>{item.tagline}</div>
                <div style={{color:T.textMuted,fontSize:"0.7rem",marginTop:"2px"}}>Saved {new Date(item.savedAt).toLocaleDateString()} · Base: {item.rules?.baseLang||"English"}</div>
              </div>
              <div style={{display:"flex",gap:"5px",flexShrink:0,flexWrap:"wrap"}}>
                <button onClick={()=>loadVault(item)} style={{background:T.accentDim,border:"none",color:T.accent,borderRadius:"7px",padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.8rem",fontWeight:700}}>⬆ Load</button>
                <button onClick={()=>downloadTxt(`${item.langName.replace(/\s+/g,"_")}_language.txt`, makeTranscript(item.result,item.rules))}
                  style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textDim,borderRadius:"7px",padding:"6px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.78rem"}}>⬇ Language</button>
                {item.glyphCount>0&&<button onClick={()=>downloadTxt(`${(item.scriptName||item.langName).replace(/\s+/g,"_")}_script.txt`, makeScriptTranscript(item.glyphs||{}, item.scriptName||item.langName, item.rules))}
                  style={{background:"transparent",border:`1px solid ${T.greenBorder}`,color:T.green,borderRadius:"7px",padding:"6px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.78rem"}}>⬇ Script</button>}
                <button onClick={()=>delSaved(item.key)} style={{background:"transparent",border:"1px solid #c05050",color:"#e05050",borderRadius:"7px",padding:"6px 8px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.78rem"}}>✕</button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );

  const main = {maxWidth:"1000px",margin:"0 auto",padding:"20px 16px 60px"};
  const pageHead = (title,desc) => (
    <div style={{marginBottom:"20px",paddingBottom:"14px",borderBottom:`1px solid ${T.border}`}}>
      <h1 style={{fontSize:"clamp(1.4rem,4vw,2rem)",fontWeight:900,color:T.accent,letterSpacing:"0.08em",marginBottom:"4px"}}>{title}</h1>
      <p style={{color:T.textDim,fontSize:"0.9rem"}}>{desc}</p>
    </div>
  );
  const forgeBtn = (text, onClick, loading=false, disabled=false) => (
    <button onClick={onClick} disabled={disabled||loading} className="forge-btn"
      style={{width:"100%",marginTop:"12px",padding:"12px",background:T.forgeGrad,border:`1px solid ${T.accent}`,borderRadius:"10px",color:"#fff",fontSize:"0.96rem",fontWeight:700,letterSpacing:"0.1em",cursor:(disabled||loading)?"not-allowed":"pointer",fontFamily:"inherit",opacity:(disabled||loading)?0.75:1}}>
      {loading?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"10px"}}><span className="forge-spinner"/>{text}</span>:text}
    </button>
  );

  return (
    <Ctx.Provider value={T}>
      <div style={{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'Lato',sans-serif"}}>
        <style>{css}</style>
        <NavBar/>
        {vaultOpen&&<VaultDrawer/>}

        {/* Name Dialogs */}
        {langDialog && (
          <NameDialog
            title="💾 Name Your Language"
            defaultValue={result?.langName || ""}
            placeholder='e.g. "Aethran", "Vroki", "Solketh"'
            onConfirm={doSaveLang}
            onCancel={() => setLangDialog(false)}
          />
        )}
        {scriptDialog && (
          <NameDialog
            title="✍️ Name Your Script"
            defaultValue={scriptName}
            placeholder='e.g. "Solar Runes", "Vrokian Glyphs"'
            onConfirm={doSaveScript}
            onCancel={() => setScriptDialog(false)}
          />
        )}

        {/* HOME */}
        {page==="home"&&<HomePage onStart={(p)=>setPage(p||"forge")} T={T}/>}

        {/* FORGE */}
        {page==="forge"&&(
          <div style={main} className="anim">
            {pageHead("⚒️ Language Forge","Set transformation rules — AI builds your complete language with guide, vocabulary, and sample sentences.")}

            <Card>
              <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700,marginBottom:"12px"}}>Language Settings</h2>
              <div style={{display:"flex",gap:"10px",marginBottom:"12px",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:"180px"}}><label style={{display:"block",fontSize:"0.72rem",color:T.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"4px"}}>Base Language</label><TInput value={rules.baseLang} onChange={e=>updateRule("baseLang",e.target.value)} placeholder="English"/></div>
                <div style={{flex:1,minWidth:"180px"}}><label style={{display:"block",fontSize:"0.72rem",color:T.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"4px"}}>Language Name Hint</label><TInput value={rules.langNameHint} onChange={e=>updateRule("langNameHint",e.target.value)} placeholder='"Aethran", "Vroki"…'/></div>
              </div>

              <div style={{display:"flex",gap:"5px",flexWrap:"wrap",marginBottom:"10px"}}>
                {CATS.map(c=><TTab key={c.key} label={`${c.icon} ${c.label}`} active={activeTab===c.key} onClick={()=>setActiveTab(c.key)}/>)}
              </div>
              {CATS.map(c=>activeTab===c.key&&<div key={c.key}><p style={{color:T.textDim,fontSize:"0.85rem",fontStyle:"italic",marginBottom:"5px"}}>{c.desc}</p><TTextarea value={rules[c.key]} onChange={e=>updateRule(c.key,e.target.value)} placeholder={c.ph}/></div>)}
              {forgeBtn(forgeLoading?"Forging your language…":"⚒️ FORGE THIS LANGUAGE", runForge, forgeLoading)}
              {forgeError&&<p style={{color:"#e05050",marginTop:"8px",textAlign:"center",fontSize:"0.88rem"}}>{forgeError}</p>}
            </Card>

            {result&&<div ref={resultRef} className="anim">
              <Card>
                <div style={{textAlign:"center",padding:"16px",background:T.card,border:`1px solid ${T.accentDim}`,borderRadius:"12px",marginBottom:"14px"}}>
                  <span style={{display:"block",fontSize:"clamp(1.8rem,5vw,2.8rem)",fontWeight:900,color:T.accent,letterSpacing:"0.18em",marginBottom:"4px"}}>{result.langName}</span>
                  <span style={{color:T.textDim,fontStyle:"italic",fontSize:"0.9rem"}}>{result.tagline}</span>
                </div>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700,marginBottom:"10px"}}>🔀 Transformation Steps</h2>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:"10px"}}>
                  {result.transformationSteps?.map((t,i)=>(
                    <div key={i} style={{background:T.card,borderRadius:"10px",padding:"12px",border:`1px solid ${T.border}`}}>
                      <div style={{color:T.textDim,fontStyle:"italic",marginBottom:"6px",fontSize:"0.86rem"}}>"{t.original}"</div>
                      {t.steps?.map((s,j)=><div key={j} style={{display:"flex",gap:"6px",fontSize:"0.78rem",color:T.purpleL,marginBottom:"3px",alignItems:"flex-start"}}><span style={{background:T.accentDim,color:T.accent,borderRadius:"50%",width:"15px",height:"15px",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:"0.6rem",flexShrink:0,marginTop:"1px"}}>{j+1}</span>{s}</div>)}
                      <div style={{color:T.accent,fontWeight:700,marginTop:"5px",fontSize:"0.88rem"}}>→ "{t.result}"</div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700,marginBottom:"12px"}}>📖 Learner's Guide to {result.langName}</h2>
                <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
                  <nav style={{display:"flex",flexDirection:"column",gap:"4px",flex:"0 0 130px",minWidth:"130px"}}>
                    {GSEC.map((s,i)=><TTab key={i} label={s.t} active={activeSection===i} onClick={()=>setActiveSection(i)}/>)}
                  </nav>
                  <div style={{flex:1,minWidth:"220px",background:T.card,border:`1px solid ${T.border}`,borderRadius:"10px",padding:"14px",minHeight:"220px"}}>
                    <h3 style={{color:T.accent,fontSize:"0.95rem",marginBottom:"10px",paddingBottom:"6px",borderBottom:`1px solid ${T.border}`}}>{GSEC[activeSection]?.t}</h3>
                    {GSEC[activeSection]?.c}
                  </div>
                </div>
              </Card>

              <Card style={{border:`1px solid ${T.accentDim}`}}>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700,marginBottom:"14px"}}>📜 Save Your Tongue</h2>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:"10px",marginBottom:"12px"}}>
                  {[
                    {style:{background:T.isDark?"linear-gradient(135deg,#0c2a1a,#071a0e)":"linear-gradient(135deg,#e8f8ee,#d0f0dc)",border:`1px solid ${T.greenBorder}`},icon:"🗄️",label:"Save to Vault",sub:"Name it & store it",fn:handleSave},
                    {style:{background:T.isDark?"linear-gradient(135deg,#6b520e,#2a1a03)":"linear-gradient(135deg,#fef3d0,#fde8a0)",border:`1px solid ${T.accent}`},icon:"⬇️",label:"Download .txt",sub:"Full rules + guide",fn:handleDownload},
                    {style:{background:T.isDark?"linear-gradient(135deg,#1a1040,#100a28)":"linear-gradient(135deg,#ece8f8,#ddd5f0)",border:`1px solid ${T.purple}`},icon:"📋",label:copyMsg||"Copy Transcript",sub:"Paste anywhere",fn:handleCopy},
                  ].map((b,i)=>(
                    <button key={i} onClick={b.fn} className="action-btn" style={{...b.style,borderRadius:"10px",color:T.text,padding:"12px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:"10px",textAlign:"left"}}>
                      <span style={{fontSize:"1.4rem"}}>{b.icon}</span>
                      <div><div style={{fontWeight:700,fontSize:"0.86rem"}}>{b.label}</div><div style={{fontSize:"0.7rem",color:T.textDim,marginTop:"2px"}}>{b.sub}</div></div>
                    </button>
                  ))}
                </div>
                {saveMsg&&<div style={{background:T.greenDark,border:`1px solid ${T.greenBorder}`,color:T.green,borderRadius:"8px",padding:"8px 14px",textAlign:"center",fontSize:"0.86rem"}}>{saveMsg}</div>}
              </Card>
            </div>}
          </div>
        )}

        {/* SCRIPT */}
        {page==="script"&&(
          <div style={main} className="anim">
            {pageHead("✍️ Script Forge","Draw unique glyphs for each character. Use shapes as a base, then personalise with freehand strokes.")}
            <Card>
              <div style={{display:"flex",gap:"16px",flexWrap:"wrap"}}>
                {/* Char picker */}
                <div style={{flex:"0 0 auto"}}>
                  <div style={{display:"flex",gap:"5px",marginBottom:"8px"}}>
                    {[["alpha","A–Z"],["num","0–9"],["sym","Sym"]].map(([k,l])=><TTab key={k} label={l} active={charSet===k} onClick={()=>setCharSet(k)}/>)}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(40px,1fr))",gap:"3px",maxWidth:"230px"}}>
                    {charGroups[charSet].map(ch=>(
                      <button key={ch} onClick={()=>setSelChar(ch)} title={ch}
                        style={{position:"relative",width:"40px",height:"40px",background:selChar===ch?T.accentDim:T.card,border:`1px solid ${glyphs[ch]?T.greenBorder:selChar===ch?T.accent:T.border}`,borderRadius:"7px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                        {glyphs[ch]?<img src={glyphs[ch]} alt={ch} style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:"0.76rem",fontWeight:700,color:selChar===ch?T.accent:T.textDim}}>{ch}</span>}
                        {glyphs[ch]&&<span style={{position:"absolute",bottom:0,right:1,fontSize:"0.42rem",color:T.green}}>✓</span>}
                      </button>
                    ))}
                  </div>
                  <div style={{marginTop:"6px",fontSize:"0.76rem",color:T.textDim}}><span style={{color:T.green}}>{assignedCount}</span> / {ALL_CHARS.length} assigned</div>
                  <div style={{marginTop:"4px",fontSize:"0.7rem",color:T.textMuted,fontStyle:"italic"}}>
                    ↵ Enter · → Arrow · Tab — next char<br/>← Arrow — previous char
                  </div>
                </div>

                {/* Canvas */}
                <div style={{flex:"0 0 auto"}}>
                  <div style={{fontSize:"0.76rem",color:T.textDim,textAlign:"center",marginBottom:"5px",letterSpacing:"0.1em"}}>DRAWING: <span style={{color:T.accent,fontSize:"1.4rem",fontWeight:900}}>{selChar}</span></div>
                  <div id="draw-canvas-wrap">
                    <DrawCanvas id="draw-canvas" onSave={saveGlyph} color={brushColor} size={brushSize} canvasSize={220}/>
                  </div>
                  {/* Brush */}
                  <div style={{marginTop:"10px",display:"flex",flexDirection:"column",gap:"7px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                      <span style={{fontSize:"0.7rem",color:T.textDim,textTransform:"uppercase",letterSpacing:"0.08em",whiteSpace:"nowrap"}}>Brush</span>
                      <input type="range" min={1} max={12} step={0.5} value={brushSize} onChange={e=>setBrushSize(parseFloat(e.target.value))} style={{flex:1,accentColor:T.accent}}/>
                      <span style={{color:T.textDim,fontSize:"0.78rem",minWidth:"20px"}}>{brushSize}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:"5px",flexWrap:"wrap"}}>
                      <span style={{fontSize:"0.7rem",color:T.textDim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Color</span>
                      {["#d4a843","#e8dfc8","#a88fe0","#3db87a","#e05050","#50aacc","#ff9040","#ff60a0","#ffffff","#333333"].map(col=>(
                        <button key={col} onClick={()=>setBrushColor(col)} style={{width:"20px",height:"20px",borderRadius:"50%",background:col,border:brushColor===col?`2px solid ${T.accent}`:`1px solid ${T.border}`,cursor:"pointer",flexShrink:0}}/>
                      ))}
                    </div>
                    {/* Shapes */}
                    <div>
                      <div style={{fontSize:"0.7rem",color:T.textDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"5px"}}>Add Shapes — click to stamp</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                        {SHAPES.map(sh=>(
                          <button key={sh.id} title={sh.id} onClick={()=>handleStamp(sh.id)}
                            className={"shape-btn"+(selShape===sh.id?" on":"")}
                            style={{width:"32px",height:"32px",background:selShape===sh.id?T.accentDim:T.card,border:`1px solid ${selShape===sh.id?T.accent:T.border}`,borderRadius:"7px",cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:"3px",transition:"all 0.1s"}}>
                            <svg viewBox="0 0 40 40" width="22" height="22" xmlns="http://www.w3.org/2000/svg" dangerouslySetInnerHTML={{__html:sh.svg(T.accent,"2")}}/>
                          </button>
                        ))}
                      </div>
                      <div style={{fontSize:"0.68rem",color:T.textDim,marginTop:"3px",fontStyle:"italic"}}>Stamp then draw on top to personalise</div>
                    </div>
                  </div>
                </div>

                {/* Assigned glyphs */}
                <div style={{flex:1,minWidth:"140px"}}>
                  <div style={{fontSize:"0.74rem",color:T.textDim,letterSpacing:"0.1em",marginBottom:"6px"}}>ASSIGNED GLYPHS</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"4px",maxHeight:"280px",overflowY:"auto"}}>
                    {ALL_CHARS.filter(c=>glyphs[c]).map(c=>(
                      <div key={c} style={{position:"relative",background:T.surface,border:`1px solid ${T.border}`,borderRadius:"6px",width:"40px",height:"48px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"2px"}}>
                        <img src={glyphs[c]} alt={c} style={{width:"28px",height:"28px",objectFit:"contain"}}/>
                        <span style={{color:T.textDim,fontSize:"0.52rem"}}>{c}</span>
                        <button onClick={()=>clearGlyph(c)} style={{position:"absolute",top:"-5px",right:"-5px",background:T.isDark?"#2a0808":"#fff0f0",border:"1px solid #c05050",color:"#e05050",borderRadius:"50%",width:"13px",height:"13px",fontSize:"0.48rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>✕</button>
                      </div>
                    ))}
                    {assignedCount===0&&<p style={{color:T.textDim,fontSize:"0.82rem",fontStyle:"italic"}}>Draw a glyph and click ✓ Assign</p>}
                  </div>

                  {/* Preview */}
                  {assignedCount>0&&<div style={{marginTop:"12px"}}>
                    <div style={{fontSize:"0.74rem",color:T.textDim,letterSpacing:"0.1em",marginBottom:"5px"}}>LIVE PREVIEW</div>
                    <TInput value={scriptName} onChange={e=>setScriptName(e.target.value)} placeholder="Type to preview…" style={{marginBottom:"5px"}}/>
                    <div style={{background:T.canvas,borderRadius:"8px",padding:"7px",border:`1px solid ${T.border}`,display:"flex",flexWrap:"wrap",gap:"2px",minHeight:"44px",alignItems:"center"}}>
                      {scriptName.toUpperCase().split("").map((c,i)=>
                        glyphs[c]?<img key={i} src={glyphs[c]} alt={c} style={{width:"30px",height:"30px",objectFit:"contain"}}/>
                        :<span key={i} style={{width:"16px",height:"30px",display:"inline-flex",alignItems:"center",justifyContent:"center",color:c===" "?"transparent":T.accentDim,fontSize:"0.72rem"}}>{c===" "?"·":c}</span>
                      )}
                    </div>
                  </div>}
                </div>
              </div>
              {scriptMsg&&<div style={{background:T.greenDark,border:`1px solid ${T.greenBorder}`,color:T.green,borderRadius:"8px",padding:"8px 14px",textAlign:"center",fontSize:"0.84rem",marginTop:"10px"}}>{scriptMsg}</div>}
            </Card>

            {assignedCount>0&&<Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"8px",marginBottom:"8px"}}>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700}}>Script Actions</h2>
                <span style={{color:T.textDim,fontSize:"0.78rem"}}>{assignedCount} glyphs · {scriptName}</span>
              </div>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                <button onClick={saveScript} className="action-btn" style={{background:T.isDark?"linear-gradient(135deg,#6b520e,#2a1a03)":"linear-gradient(135deg,#fef3d0,#fde8a0)",border:`1px solid ${T.accent}`,borderRadius:"10px",color:T.text,padding:"10px 16px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:"8px"}}>
                  <span>💾</span><div><div style={{fontWeight:700,fontSize:"0.85rem"}}>Save Script</div><div style={{fontSize:"0.7rem",color:T.textDim}}>Name it & store</div></div>
                </button>
                <button onClick={()=>downloadTxt(`${scriptName.replace(/\s+/g,"_")}_script.txt`, makeScriptTranscript(glyphs, scriptName, rules))} className="action-btn"
                  style={{background:T.isDark?"linear-gradient(135deg,#0c2a1a,#071a0e)":"linear-gradient(135deg,#e8f8ee,#d0f0dc)",border:`1px solid ${T.greenBorder}`,borderRadius:"10px",color:T.text,padding:"10px 16px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:"8px"}}>
                  <span>⬇️</span><div><div style={{fontWeight:700,fontSize:"0.85rem"}}>Download Script</div><div style={{fontSize:"0.7rem",color:T.textDim}}>Full glyph map .txt</div></div>
                </button>
                <button onClick={()=>setPage("practice")} className="action-btn" style={{background:T.forgeGrad,border:`1px solid ${T.accent}`,borderRadius:"10px",color:"#fff",padding:"10px 16px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:"8px"}}>
                  <span>🎯</span><div><div style={{fontWeight:700,fontSize:"0.85rem"}}>Practice Mode</div><div style={{fontSize:"0.7rem",opacity:0.8}}>Use your script</div></div>
                </button>
              </div>
            </Card>}
          </div>
        )}

        {/* PRACTICE */}
        {page==="practice"&&(
          <div style={main} className="anim">
            {pageHead("🎯 Practice Mode","Sentences appear in your custom glyphs. Read each symbol, recognise its letter, type the words back.")}

            <Card>
              <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700,marginBottom:"12px"}}>Setup</h2>
              <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"12px"}}>
                <div style={{flex:1,minWidth:"180px"}}>
                  <label style={{display:"block",fontSize:"0.72rem",color:T.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"5px"}}>Difficulty</label>
                  <div style={{display:"flex",gap:"5px"}}>
                    {["beginner","intermediate","advanced"].map(l=><TTab key={l} label={l.charAt(0).toUpperCase()+l.slice(1)} active={practiceLevel===l} onClick={()=>setPracticeLevel(l)}/>)}
                  </div>
                </div>
                <div style={{flex:1,minWidth:"180px"}}>
                  <label style={{display:"block",fontSize:"0.72rem",color:T.textDim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"5px"}}>Keyboard Layout</label>
                  <div style={{display:"flex",gap:"5px"}}>
                    <TTab label="⌨️ QWERTY" active={kbLayout==="qwerty"} onClick={()=>setKbLayout("qwerty")}/>
                    <TTab label="🔤 A–Z" active={kbLayout==="abc"} onClick={()=>setKbLayout("abc")}/>
                  </div>
                </div>
              </div>
              {forgeBtn(practiceLoading?"Generating sentences…":"🎯 GENERATE SENTENCES", runPractice, practiceLoading)}
              {practiceError&&<p style={{color:"#e05050",marginTop:"8px",fontSize:"0.86rem"}}>{practiceError}</p>}
            </Card>

            {assignedCount>0&&<Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px",flexWrap:"wrap",gap:"5px"}}>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700}}>🔤 Glyph Reference Card</h2>
                <span style={{color:T.textDim,fontSize:"0.76rem"}}>{assignedCount} glyphs assigned</span>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
                {ALL_CHARS.filter(c=>glyphs[c]).map(c=>(
                  <div key={c} style={{background:T.canvas,border:`1px solid ${T.border}`,borderRadius:"6px",padding:"3px",textAlign:"center",width:"38px"}}>
                    <img src={glyphs[c]} alt={c} style={{width:"24px",height:"24px",objectFit:"contain",display:"block",margin:"0 auto"}}/>
                    <div style={{color:T.accentDim,fontSize:"0.58rem",marginTop:"1px"}}>{c}</div>
                  </div>
                ))}
              </div>
            </Card>}

            {sentences.length>0&&<Card>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"14px",flexWrap:"wrap",gap:"6px"}}>
                <h2 style={{color:T.accent,fontSize:"1rem",fontWeight:700}}>📝 {sentences.length} Sentences</h2>
                <span style={{color:T.textDim,fontSize:"0.8rem"}}>Each glyph = its letter</span>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:"14px"}}>
                {sentences.map(s=><PracticeItem key={s.id} s={s} glyphs={glyphs} kb={kbLayout}/>)}
              </div>
            </Card>}
          </div>
        )}

        <footer style={{textAlign:"center",padding:"24px",color:T.textMuted,fontSize:"0.75rem",letterSpacing:"0.1em",borderTop:`1px solid ${T.border}`}}>
          LinguaForge — Free forever · No login · Your language, your script
        </footer>
      </div>
    </Ctx.Provider>
  );
}
